import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ZoomIn, ZoomOut, Check, X, Move } from "lucide-react";
import { Slider } from "@/components/ui/slider";

/**
 * ImageCropper component - Square crop with rounded corners, zoom and pan
 * The crop area is fixed square with rounded corners (rounded-2xl style)
 */
export default function ImageCropper({ 
  open, 
  onClose, 
  imageSrc, 
  onCropComplete,
  aspectRatio = 1 
}) {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  
  const CROP_SIZE = 280; // Fixed crop area size
  const BORDER_RADIUS = 24; // Rounded corners (like rounded-2xl)

  // Reset on open
  useEffect(() => {
    if (open) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setImageLoaded(false);
    }
  }, [open]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Limit movement based on zoom
    const maxOffset = CROP_SIZE * (zoom - 1) / 2 + 50;
    
    setPosition({
      x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
      y: Math.max(-maxOffset, Math.min(maxOffset, newY))
    });
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y
    });
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    const maxOffset = CROP_SIZE * (zoom - 1) / 2 + 50;
    
    setPosition({
      x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
      y: Math.max(-maxOffset, Math.min(maxOffset, newY))
    });
  }, [isDragging, dragStart, zoom]);

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  const getCroppedImg = useCallback(async () => {
    if (!imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return null;

    const outputSize = 400;
    canvas.width = outputSize;
    canvas.height = outputSize;

    // Calculate the visible portion based on zoom and position
    const scale = zoom;
    const imgWidth = image.naturalWidth;
    const imgHeight = image.naturalHeight;
    
    // Find the center point in the original image
    const displayWidth = CROP_SIZE * scale;
    const displayHeight = CROP_SIZE * scale;
    
    // The position offset in original image coordinates
    const scaleRatio = Math.min(imgWidth, imgHeight) / CROP_SIZE;
    const offsetX = (-position.x / scale) * scaleRatio;
    const offsetY = (-position.y / scale) * scaleRatio;
    
    // Source rectangle in original image
    const sourceSize = Math.min(imgWidth, imgHeight) / scale;
    const sourceX = (imgWidth - sourceSize) / 2 + offsetX;
    const sourceY = (imgHeight - sourceSize) / 2 + offsetY;

    // Enable smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Create rounded rectangle clip (instead of circular)
    const radius = (BORDER_RADIUS / CROP_SIZE) * outputSize; // Scale border radius to output size
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(outputSize - radius, 0);
    ctx.quadraticCurveTo(outputSize, 0, outputSize, radius);
    ctx.lineTo(outputSize, outputSize - radius);
    ctx.quadraticCurveTo(outputSize, outputSize, outputSize - radius, outputSize);
    ctx.lineTo(radius, outputSize);
    ctx.quadraticCurveTo(0, outputSize, 0, outputSize - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.clip();

    // Draw the cropped portion
    ctx.drawImage(
      image,
      Math.max(0, sourceX),
      Math.max(0, sourceY),
      Math.min(sourceSize, imgWidth - Math.max(0, sourceX)),
      Math.min(sourceSize, imgHeight - Math.max(0, sourceY)),
      0,
      0,
      outputSize,
      outputSize
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/png", 1);
    });
  }, [zoom, position]);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (croppedBlob) {
        onCropComplete(croppedBlob);
        onClose();
      }
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!imageSrc) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[420px] rounded-3xl bg-white dark:bg-[#1C1C1E] p-0 border-0 max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-xl font-bold text-[#1D1D1F] dark:text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FF6B4A]/10 flex items-center justify-center">
                <Move className="text-[#FF6B4A]" size={20} />
              </div>
              Posiziona la Foto
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm text-[#86868B] mb-4">
            Trascina l'immagine per posizionarla e usa lo slider per lo zoom.
          </p>

          {/* Crop Area - Square with rounded corners */}
          <div 
            ref={containerRef}
            className="relative bg-[#F5F5F7] dark:bg-black/50 rounded-2xl overflow-hidden flex items-center justify-center mx-auto"
            style={{ width: CROP_SIZE + 40, height: CROP_SIZE + 40 }}
          >
            {/* Dark overlay OUTSIDE the crop area - the area to discard is dark */}
            <div 
              className="absolute inset-0 z-10 pointer-events-none"
              style={{
                boxShadow: `0 0 0 9999px rgba(0,0,0,0.7)`,
                width: CROP_SIZE,
                height: CROP_SIZE,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: BORDER_RADIUS,
              }}
            />
            
            {/* Square border indicator with rounded corners */}
            <div 
              className="absolute z-20 pointer-events-none border-2 border-white"
              style={{
                width: CROP_SIZE,
                height: CROP_SIZE,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: BORDER_RADIUS,
                boxShadow: '0 0 0 4px rgba(255,107,74,0.5)'
              }}
            />

            {/* Draggable Image */}
            <div
              className="cursor-move select-none touch-none"
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Immagine da ritagliare"
                onLoad={() => setImageLoaded(true)}
                style={{ 
                  width: CROP_SIZE, 
                  height: CROP_SIZE, 
                  objectFit: 'cover',
                  pointerEvents: 'none'
                }}
                crossOrigin="anonymous"
                draggable={false}
              />
            </div>
          </div>

          {/* Zoom Slider */}
          <div className="mt-6 flex items-center gap-4">
            <ZoomOut className="text-[#86868B]" size={18} />
            <Slider
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0])}
              min={1}
              max={3}
              step={0.1}
              className="flex-1"
            />
            <ZoomIn className="text-[#86868B]" size={18} />
          </div>
          
          <p className="text-xs text-center text-[#86868B] mt-2">
            Zoom: {Math.round(zoom * 100)}%
          </p>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-xl h-12"
              data-testid="crop-cancel-btn"
            >
              <X size={16} className="mr-2" />
              Annulla
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isProcessing || !imageLoaded}
              className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl h-12"
              data-testid="crop-confirm-btn"
            >
              {isProcessing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Check size={16} className="mr-2" />
              )}
              {isProcessing ? "Elaborazione..." : "Conferma"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
